import React, { useEffect, useMemo, useState } from "react";
import { Download, Plus, Printer, RefreshCw, Save, Trash2, X } from "lucide-react";

import api from "../../api";

function blankLot(rate) {
  return {
    serial_no: "",
    silver_type: "OLD_SILVER",
    description: "",
    gross_weight_g: "",
    container_weight_g: "",
    net_weight_g: 0,
    purity_pct: "",
    net_silver_g: 0,
    melting_point_c: "",
    assay_certificate: "",
    market_rate_per_g: rate || "",
    melt_value_rs: 0,
  };
}

function enrichLot(item, fallbackRate) {
  const gross = Number(item.gross_weight_g || 0);
  const container = Number(item.container_weight_g || 0);
  const purity = Number(item.purity_pct || 0);
  const rate = Number(item.market_rate_per_g || fallbackRate || 0);
  const netWeight = Math.max(0, gross - container);
  const netSilver = (netWeight * purity) / 100;
  const meltValue = netSilver * rate;
  return {
    ...item,
    gross_weight_g: item.gross_weight_g,
    container_weight_g: item.container_weight_g,
    purity_pct: item.purity_pct,
    market_rate_per_g: item.market_rate_per_g || String(fallbackRate || ""),
    net_weight_g: Number(netWeight.toFixed(3)),
    net_silver_g: Number(netSilver.toFixed(3)),
    melt_value_rs: Number(meltValue.toFixed(2)),
  };
}

function amount(value) {
  return `Rs. ${Number(value || 0).toFixed(2)}`;
}

function grams(value) {
  return `${Number(value || 0).toFixed(3)} g`;
}

function ratePerGram(value) {
  return `${amount(value)} / g`;
}

function SilverIntake() {
  const [customers, setCustomers] = useState([]);
  const [receipts, setReceipts] = useState([]);
  const [customerTransactions, setCustomerTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [marketRate, setMarketRate] = useState(0);
  const [selectedCustomerId, setSelectedCustomerId] = useState("");
  const [customerBalance, setCustomerBalance] = useState(0);
  const [customerLimit, setCustomerLimit] = useState(0);
  const [statementSummary, setStatementSummary] = useState(null);
  const [statementDateFrom, setStatementDateFrom] = useState("");
  const [statementDateTo, setStatementDateTo] = useState("");
  const [lastReceipt, setLastReceipt] = useState(null);
  const [form, setForm] = useState({ customer_id: "", notes: "", items: [blankLot("")] });

  const currentCustomer = useMemo(
    () => customers.find((customer) => String(customer.id) === String(selectedCustomerId)) || null,
    [customers, selectedCustomerId]
  );

  const filteredReceipts = useMemo(() => {
    return receipts.filter((row) => {
      if (selectedCustomerId && String(row.customer_id) !== String(selectedCustomerId)) return false;
      if (statementDateFrom && String(row.receipt_date || "") < statementDateFrom) return false;
      if (statementDateTo && String(row.receipt_date || "") > statementDateTo) return false;
      return true;
    });
  }, [receipts, selectedCustomerId, statementDateFrom, statementDateTo]);

  const items = useMemo(
    () => form.items.map((item) => enrichLot(item, marketRate)),
    [form.items, marketRate]
  );

  const totals = useMemo(() => ({
    gross: items.reduce((sum, item) => sum + Number(item.gross_weight_g || 0), 0),
    net: items.reduce((sum, item) => sum + Number(item.net_weight_g || 0), 0),
    netSilver: items.reduce((sum, item) => sum + Number(item.net_silver_g || 0), 0),
    meltValue: items.reduce((sum, item) => sum + Number(item.melt_value_rs || 0), 0),
  }), [items]);

  const load = async () => {
    setLoading(true);
    try {
      const [metaRes, listRes, rateRes] = await Promise.all([
        api.get("/accounts/invoices/meta"),
        api.get("/silver-intake"),
        api.get("/metal-exchange/market-rates?metal_type=SILVER"),
      ]);
      const nextRate = Number(rateRes.data?.latest?.rate_per_gram || listRes.data?.latest_market_rate || 0);
      const customerRows = metaRes.data?.customers || [];
      setCustomers(customerRows);
      setReceipts(listRes.data?.data || []);
      setMarketRate(nextRate);
      const nextCustomerId = selectedCustomerId || String(customerRows[0]?.id || "");
      setSelectedCustomerId(nextCustomerId);
      setForm((prev) => ({
        ...prev,
        customer_id: prev.customer_id || nextCustomerId,
        items: prev.items.map((item) => enrichLot({ ...item, market_rate_per_g: item.market_rate_per_g || String(nextRate || "") }, nextRate)),
      }));
      setError("");
    } catch (err) {
      setError(err.response?.data?.error || "Failed to load silver intake setup.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    const loadCustomerData = async () => {
      if (!selectedCustomerId) {
        setCustomerTransactions([]);
        setCustomerBalance(0);
        setCustomerLimit(0);
        setStatementSummary(null);
        return;
      }
      try {
        const params = {};
        if (statementDateFrom) params.date_from = statementDateFrom;
        if (statementDateTo) params.date_to = statementDateTo;
        const res = await api.get(`/silver-intake/customer-transactions?customer_id=${selectedCustomerId}`, { params });
        setCustomerTransactions(res.data?.data || []);
        setCustomerBalance(Number(res.data?.customer?.current_silver_reserve_grams || 0));
        setCustomerLimit(Number(res.data?.customer?.silver_intake_credit_limit_grams || 0));
        setStatementSummary(res.data?.summary || null);
      } catch {
        setCustomerTransactions([]);
        setCustomerBalance(0);
        setCustomerLimit(0);
        setStatementSummary(null);
      }
    };
    loadCustomerData();
  }, [selectedCustomerId, statementDateFrom, statementDateTo]);

  const openCreateModal = () => {
    setForm({
      customer_id: selectedCustomerId,
      notes: "",
      items: [enrichLot(blankLot(String(marketRate || "")), marketRate)],
    });
    setShowCreateModal(true);
    setError("");
    setMessage("");
  };

  const closeCreateModal = () => {
    if (saving) return;
    setShowCreateModal(false);
    setForm({
      customer_id: selectedCustomerId,
      notes: "",
      items: [enrichLot(blankLot(String(marketRate || "")), marketRate)],
    });
  };

  const updateItem = (index, key, value) => {
    setForm((prev) => ({
      ...prev,
      items: prev.items.map((item, itemIndex) => itemIndex === index ? enrichLot({ ...item, [key]: value }, marketRate) : item),
    }));
  };

  const addLot = () => setForm((prev) => ({ ...prev, items: [...prev.items, enrichLot(blankLot(String(marketRate || "")), marketRate)] }));
  const removeLot = (index) => setForm((prev) => ({ ...prev, items: prev.items.filter((_, itemIndex) => itemIndex !== index) || [enrichLot(blankLot(String(marketRate || "")), marketRate)] }));
  const handleMarketRateChange = (value) => {
    setMarketRate(value);
    setForm((prev) => ({
      ...prev,
      items: prev.items.map((item) => enrichLot({ ...item, market_rate_per_g: String(value || "") }, value)),
    }));
  };

  const saveReceipt = async () => {
    setSaving(true);
    try {
      const payload = {
        customer_id: Number(form.customer_id),
        notes: form.notes,
        receipt_date: new Date().toISOString().slice(0, 10),
        items: items.map((item) => ({
          serial_no: item.serial_no,
          silver_type: item.silver_type,
          description: item.description,
          gross_weight_g: Number(item.gross_weight_g || 0),
          container_weight_g: Number(item.container_weight_g || 0),
          purity_pct: Number(item.purity_pct || 0),
          melting_point_c: item.melting_point_c === "" ? null : Number(item.melting_point_c),
          assay_certificate: item.assay_certificate,
          market_rate_per_g: Number(item.market_rate_per_g || marketRate || 0),
        })),
      };
      const res = await api.post("/silver-intake", payload);
      setLastReceipt({
        ...res.data,
        customer_name: currentCustomer?.name || "",
        total_gross_weight_g: totals.gross,
        total_net_weight_g: totals.net,
      });
      setMessage(`Silver intake receipt ${res.data.receipt_no} created.`);
      setSelectedCustomerId(String(payload.customer_id));
      setShowCreateModal(false);
      await load();
    } catch (err) {
      setError(err.response?.data?.error || "Failed to save silver intake receipt.");
    } finally {
      setSaving(false);
    }
  };

  const downloadStatement = async () => {
    if (!selectedCustomerId) return;
    try {
      const params = {};
      if (statementDateFrom) params.date_from = statementDateFrom;
      if (statementDateTo) params.date_to = statementDateTo;
      const res = await api.get(`/silver-intake/customers/${selectedCustomerId}/statement/pdf`, { params, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = `${currentCustomer?.code || selectedCustomerId}-silver-intake-statement.pdf`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to download silver intake statement.");
    }
  };

  const printReceipt = () => {
    if (!lastReceipt?.id) return;
    api.get(`/silver-intake/${lastReceipt.id}/pdf`, { responseType: "blob" })
      .then((res) => {
        const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
        const link = document.createElement("a");
        link.href = url;
        link.download = `${lastReceipt.receipt_no || "silver-intake-receipt"}.pdf`;
        link.click();
        URL.revokeObjectURL(url);
      })
      .catch((err) => {
        setError(err.response?.data?.error || "Failed to download silver intake receipt PDF.");
      });
  };

  return (
    <div className="space-y-6">
      {(message || error) && (
        <div className={`rounded-2xl border px-4 py-3 text-sm ${error ? "border-rose-200 bg-rose-50 text-rose-700" : "border-emerald-200 bg-emerald-50 text-emerald-700"}`}>
          {error || message}
        </div>
      )}

      <section className="rounded-[2rem] border border-slate-200 bg-[linear-gradient(135deg,#0f172a_0%,#14532d_55%,#22c55e_100%)] px-6 py-6 text-white shadow-[0_24px_80px_rgba(15,23,42,0.18)]">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.3em] text-emerald-100">Silver Intake</div>
            <h2 className="mt-2 text-3xl font-bold">Customer reserve intake and production reserve ledger</h2>
            <p className="mt-2 max-w-3xl text-sm text-emerald-50">Choose a customer first, review their intake transactions on the right, and create a new intake from a popup when needed.</p>
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={load} className="secondary-btn border-white/20 bg-white/10 text-white hover:bg-white/20">
              <RefreshCw size={15} /> Refresh
            </button>
            <button type="button" onClick={openCreateModal} disabled={!selectedCustomerId} className="primary-btn bg-white text-emerald-700 hover:bg-emerald-50">
              <Plus size={15} /> New Transaction
            </button>
          </div>
        </div>
      </section>

      <section className="grid gap-4 md:grid-cols-4">
        <StatCard label="Selected Balance" value={grams(customerBalance)} />
        <StatCard label="Credit Limit" value={grams(customerLimit)} />
        <StatCard label="Today Rate / Gram" value={ratePerGram(marketRate)} />
        <StatCard label="Transactions" value={String(customerTransactions.length || 0)} />
      </section>

      <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <div className="text-lg font-bold text-slate-900">All Available Transactions</div>
            <div className="text-sm text-slate-500">Filter date-wise and review every receipt for the selected customer at the top of the page.</div>
          </div>
          <div className="flex flex-wrap items-end gap-3">
            <Field label="From">
              <input type="date" value={statementDateFrom} onChange={(event) => setStatementDateFrom(event.target.value)} className="input min-w-[160px]" />
            </Field>
            <Field label="To">
              <input type="date" value={statementDateTo} onChange={(event) => setStatementDateTo(event.target.value)} className="input min-w-[160px]" />
            </Field>
            <button type="button" onClick={downloadStatement} disabled={!selectedCustomerId} className="secondary-btn">
              <Download size={15} /> Statement PDF
            </button>
            <button type="button" onClick={printReceipt} disabled={!lastReceipt} className="secondary-btn">
              <Printer size={15} /> Print Last Receipt
            </button>
          </div>
        </div>
        <div className="mt-4 overflow-x-auto rounded-2xl border border-slate-200">
          <table className="min-w-full text-left text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-3">Receipt No</th>
                <th className="px-4 py-3">Date</th>
                <th className="px-4 py-3">Customer</th>
                <th className="px-4 py-3">Net Silver</th>
                <th className="px-4 py-3">Melt Value</th>
                <th className="px-4 py-3">Reserve After</th>
              </tr>
            </thead>
            <tbody>
              {!loading && filteredReceipts.map((row) => (
                <tr key={row.id} className="border-t border-slate-100">
                  <td className="px-4 py-3 font-semibold text-emerald-700">{row.receipt_no}</td>
                  <td className="px-4 py-3">{row.receipt_date}</td>
                  <td className="px-4 py-3">{row.customer_name}</td>
                  <td className="px-4 py-3">{grams(row.total_net_silver_g)}</td>
                  <td className="px-4 py-3">{amount(row.total_melt_value_rs)}</td>
                  <td className="px-4 py-3">{row.current_silver_reserve_display || grams(0)}</td>
                </tr>
              ))}
              {!loading && !filteredReceipts.length && (
                <tr>
                  <td colSpan="6" className="px-4 py-10 text-center text-slate-500">No transactions found for the selected filter.</td>
                </tr>
              )}
              {loading && (
                <tr>
                  <td colSpan="6" className="px-4 py-10 text-center text-slate-500">Loading transactions...</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section className="grid gap-4 lg:grid-cols-[320px,1fr]">
        <div className="rounded-3xl border border-slate-200 bg-white shadow-sm">
          <div className="border-b border-slate-100 px-5 py-4">
            <div className="text-lg font-bold text-slate-900">Available Customers</div>
            <div className="text-sm text-slate-500">Click a customer to view their reserve transactions on the right.</div>
          </div>
          <div className="max-h-[760px] overflow-auto">
            {customers.map((customer) => {
              const active = String(customer.id) === String(selectedCustomerId);
              return (
                <button
                  key={customer.id}
                  type="button"
                  onClick={() => setSelectedCustomerId(String(customer.id))}
                  className={`w-full border-b border-slate-100 px-5 py-4 text-left transition ${active ? "bg-emerald-50" : "hover:bg-slate-50"}`}
                >
                  <div className="font-semibold text-slate-900">{customer.name}</div>
                  <div className="text-xs text-slate-500">{customer.code}</div>
                </button>
              );
            })}
          </div>
        </div>

        <div className="rounded-3xl border border-slate-200 bg-white shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 px-5 py-4">
            <div>
              <div className="text-lg font-bold text-slate-900">{currentCustomer ? `${currentCustomer.name} Transactions` : "Customer Transactions"}</div>
              <div className="text-sm text-slate-500">Previous, current, and value movement for the selected customer’s silver reserve.</div>
            </div>
            <button type="button" onClick={openCreateModal} disabled={!selectedCustomerId} className="primary-btn">
              <Plus size={15} /> Create Transaction
            </button>
          </div>

          {statementSummary && (
            <div className="grid gap-3 border-b border-slate-100 px-5 py-4 md:grid-cols-3">
              <StatCard label="Previous Balance" value={statementSummary.previous_balance?.display || "0.000 g"} />
              <StatCard label="Net Total" value={statementSummary.net_total?.display || "0.000 g"} />
              <StatCard label="Closing Balance" value={statementSummary.closing_balance?.display || "0.000 g"} />
            </div>
          )}

          <div className="max-h-[640px] overflow-auto">
            <table className="min-w-full text-left text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-4 py-3">Ref</th>
                  <th className="px-4 py-3">Date</th>
                  <th className="px-4 py-3">Type</th>
                  <th className="px-4 py-3">Previous</th>
                  <th className="px-4 py-3">Movement</th>
                  <th className="px-4 py-3">Current</th>
                  <th className="px-4 py-3">Value</th>
                </tr>
              </thead>
              <tbody>
                {customerTransactions.map((transaction) => (
                  <tr key={transaction.id} className="border-t border-slate-100">
                    <td className="px-4 py-3 font-semibold text-emerald-700">{transaction.reference_no}</td>
                    <td className="px-4 py-3">{transaction.date}</td>
                    <td className="px-4 py-3">{transaction.transaction_type}</td>
                    <td className="px-4 py-3">{transaction.opening_balance_display}</td>
                    <td className="px-4 py-3">
                      <div>In: {grams(transaction.quantity_in_g)}</div>
                      <div className="text-xs text-slate-500">Out: {grams(transaction.quantity_out_g)}</div>
                    </td>
                    <td className="px-4 py-3">{transaction.closing_balance_display}</td>
                    <td className="px-4 py-3">{amount(transaction.value_rs)}</td>
                  </tr>
                ))}
                {!customerTransactions.length && (
                  <tr>
                    <td colSpan="7" className="px-4 py-10 text-center text-slate-500">Select a customer to view transactions.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/40 p-2 sm:p-4">
          <div className="max-h-[94vh] w-full max-w-[98vw] xl:max-w-[1800px] overflow-auto rounded-3xl border border-slate-200 bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-slate-100 px-6 py-5">
              <div>
                <div className="text-lg font-bold text-slate-900">Create Silver Intake Transaction</div>
                <div className="text-sm text-slate-500">Add one or more lots, then save or cancel from this popup.</div>
              </div>
              <button type="button" onClick={closeCreateModal} className="secondary-btn">
                <X size={15} /> Cancel
              </button>
            </div>

            <div className="grid gap-4 border-b border-slate-100 p-4 sm:p-6 xl:grid-cols-3">
              <Field label="Customer">
                <select value={form.customer_id} onChange={(event) => setForm((prev) => ({ ...prev, customer_id: event.target.value }))} className="input">
                  {customers.map((customer) => (
                    <option key={customer.id} value={customer.id}>
                      {customer.name} ({customer.code})
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Today Rate / Gram">
                <div className="relative">
                  <input
                    type="number"
                    value={marketRate}
                    onChange={(event) => handleMarketRateChange(event.target.value)}
                    className="input pr-16"
                    placeholder="Enter rate per gram"
                  />
                  <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-xs font-semibold text-slate-500">/ g</span>
                </div>
              </Field>
              <Field label="Notes">
                <input value={form.notes} onChange={(event) => setForm((prev) => ({ ...prev, notes: event.target.value }))} className="input" />
              </Field>
            </div>

            <div className="overflow-x-auto p-4 sm:p-6">
              <table className="min-w-[1280px] xl:min-w-[1500px] text-left text-sm">
                <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-3 py-3">Serial No</th>
                    <th className="px-3 py-3">Silver Type</th>
                    <th className="px-3 py-3">Description</th>
                    <th className="px-3 py-3">Gross Wt(g)</th>
                    <th className="px-3 py-3">Container Wt(g)</th>
                    <th className="px-3 py-3">Net Wt(g)</th>
                    <th className="px-3 py-3">Purity%</th>
                    <th className="px-3 py-3">Net Silver(g)</th>
                    <th className="px-3 py-3">Melting Point</th>
                    <th className="px-3 py-3">Rate / g</th>
                    <th className="px-3 py-3">Melt Value</th>
                    <th className="px-3 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item, index) => (
                    <tr key={index} className="border-t border-slate-100">
                      <td className="px-3 py-3"><input className="input" value={item.serial_no} onChange={(event) => updateItem(index, "serial_no", event.target.value)} /></td>
                      <td className="px-3 py-3">
                        <select className="input" value={item.silver_type} onChange={(event) => updateItem(index, "silver_type", event.target.value)}>
                          <option value="OLD_SILVER">OLD_SILVER</option>
                          <option value="FINE_SILVER">FINE_SILVER</option>
                          <option value="SILLY">SILLY</option>
                        </select>
                      </td>
                      <td className="px-3 py-3"><input className="input" value={item.description} onChange={(event) => updateItem(index, "description", event.target.value)} /></td>
                      <td className="px-3 py-3"><input type="number" className="input" value={item.gross_weight_g} onChange={(event) => updateItem(index, "gross_weight_g", event.target.value)} /></td>
                      <td className="px-3 py-3"><input type="number" className="input" value={item.container_weight_g} onChange={(event) => updateItem(index, "container_weight_g", event.target.value)} /></td>
                      <td className="px-3 py-3 font-semibold text-slate-900">{Number(item.net_weight_g || 0).toFixed(3)}</td>
                      <td className="px-3 py-3"><input type="number" className="input" value={item.purity_pct} onChange={(event) => updateItem(index, "purity_pct", event.target.value)} /></td>
                      <td className="px-3 py-3 font-semibold text-emerald-700">{Number(item.net_silver_g || 0).toFixed(3)}</td>
                      <td className="px-3 py-3"><input type="number" className="input" value={item.melting_point_c} onChange={(event) => updateItem(index, "melting_point_c", event.target.value)} /></td>
                      <td className="px-3 py-3"><input type="number" className="input" value={item.market_rate_per_g} onChange={(event) => updateItem(index, "market_rate_per_g", event.target.value)} /></td>
                      <td className="px-3 py-3 font-semibold text-slate-900">{amount(item.melt_value_rs)}</td>
                      <td className="px-3 py-3">
                        <button type="button" onClick={() => removeLot(index)} className="table-btn" disabled={items.length === 1}>
                          <Trash2 size={14} />
                        </button>
                      </td>
                    </tr>
                  ))}
                  <tr className="border-t border-slate-200 bg-slate-50 font-semibold text-slate-900">
                    <td className="px-3 py-3" colSpan="3">Totals</td>
                    <td className="px-3 py-3">{totals.gross.toFixed(3)}</td>
                    <td className="px-3 py-3">-</td>
                    <td className="px-3 py-3">{totals.net.toFixed(3)}</td>
                    <td className="px-3 py-3">-</td>
                    <td className="px-3 py-3">{totals.netSilver.toFixed(3)}</td>
                    <td className="px-3 py-3">-</td>
                    <td className="px-3 py-3">-</td>
                    <td className="px-3 py-3">{amount(totals.meltValue)}</td>
                    <td className="px-3 py-3"></td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="flex flex-wrap justify-between gap-3 border-t border-slate-100 px-4 py-4 sm:px-6">
              <button type="button" onClick={addLot} className="secondary-btn">
                <Plus size={15} /> Add Lot
              </button>
              <div className="flex gap-2">
                <button type="button" onClick={closeCreateModal} className="secondary-btn">
                  Cancel
                </button>
                <button type="button" onClick={saveReceipt} disabled={saving} className="primary-btn">
                  <Save size={15} /> Save Transaction
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .input {
          width: 100%;
          border-radius: 0.75rem;
          border: 1px solid rgb(203 213 225);
          padding: 0.65rem 0.85rem;
          background: white;
        }
        .primary-btn, .secondary-btn, .table-btn {
          display: inline-flex;
          align-items: center;
          gap: 0.4rem;
          border-radius: 0.75rem;
          padding: 0.65rem 1rem;
          font-size: 0.875rem;
          font-weight: 600;
          transition: 0.2s ease;
        }
        .primary-btn { background: rgb(22 163 74); color: white; }
        .secondary-btn, .table-btn { border: 1px solid rgb(203 213 225); background: white; color: rgb(51 65 85); }
      `}</style>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="space-y-1">
      <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</div>
      {children}
    </label>
  );
}

function StatCard({ label, value }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
      <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</div>
      <div className="mt-2 text-sm font-semibold text-slate-900">{value}</div>
    </div>
  );
}

export default SilverIntake;
